import React from 'react';
import { ChevronDownIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { AE_ICON_SIZE, AE_MUTED } from '@/components/assessment-engine/layout/aeTheme';
import { QuestionBuilderSettings } from './QuestionBuilderSettings';
import type { QuestionDraft } from './questionDraftTypes';

interface QuestionSettingsToggleProps {
  draft: QuestionDraft;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Compact settings toggle for the question header row. */
export const QuestionSettingsToggle: React.FC<QuestionSettingsToggleProps> = ({
  draft,
  open,
  onOpenChange,
}) => {
  const { t } = useLanguage();
  const typeLabel = t(`assessmentEngine.questionType.${draft.question_type}`);
  const requiredLabel = draft.is_required
    ? t('assessmentEngine.builder.required')
    : t('assessmentEngine.builder.optional');

  return (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-hover-bg)]/50 px-2 py-1 text-left text-xs text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm"
      onClick={() => onOpenChange(!open)}
      aria-expanded={open}
      aria-label={
        open
          ? t('assessmentEngine.builder.hideSettings')
          : t('assessmentEngine.builder.showSettings')
      }
    >
      <Cog6ToothIcon className={AE_ICON_SIZE} aria-hidden />
      {!open && (
        <span className={`${AE_MUTED} hidden truncate sm:inline`}>
          {typeLabel} · {requiredLabel}
        </span>
      )}
      <ChevronDownIcon
        className={`${AE_ICON_SIZE} shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        aria-hidden
      />
    </button>
  );
};

interface CollapsibleQuestionSettingsProps {
  draft: QuestionDraft;
  onChange: (patch: Partial<QuestionDraft>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scalePresets?: import('./templateConfigTypes').AeTemplateScalePreset[];
}

/** Question type & scale settings — toggle in header, panel expands below. */
export const CollapsibleQuestionSettings: React.FC<CollapsibleQuestionSettingsProps> = ({
  draft,
  onChange,
  open,
  onOpenChange,
  scalePresets,
}) => (
  <>
    <QuestionSettingsToggle draft={draft} open={open} onOpenChange={onOpenChange} />
    {open && <QuestionBuilderSettings draft={draft} onChange={onChange} scalePresets={scalePresets} />}
  </>
);
